<script setup lang="ts">
const window = useWindowSize();
const device = useDevice();

const props = defineProps<{
    title: string,
    desc: string,
    items: Array<{
        title: string,
        desc: string,
        img: string,
    }>,
}>();
const sectionRef = useTemplateRef('sectionRef');
const sectionRefOffset = useElementBounding(sectionRef);

const splitedTitle = computed(() => props.title.split("'"));

const startSticky = computed(() => {
    if (!device.isDesktop.value) return;

    if (sectionRefOffset.top.value <= 0) return true;
    return false;
})

const endSticky = computed(() => {
    if (!device.isDesktop.value) return;
    
    if (sectionRefOffset.bottom.value <= window.height.value) return true;
    return false;
})

// Meteors
const meteors = [
    { top: 200, delay: '3s' },
    { top: 400, delay: '5s' },
    { top: 600, delay: '2s' },
    { top: 800, delay: '1s' },
    { top: 1000, delay: '4s' },
    { top: 1200, delay: '3s' },
    { top: 1400, delay: '5s' },
    { top: 1600, delay: '2s' },
    { top: 1800, delay: '1s' },
    { top: 2000, delay: '4s' },
    { top: 2200, delay: '3s' },
    { top: 2400, delay: '5s' },
];
</script>

<template>
    <SectionsBase 
        ref="sectionRef" 
        id="features" 
        class="relative lg:bg-none! overflow-hidden"
    >
        <div 
            class=" blue-bg-gradient"
            :class="[
                startSticky && !endSticky && 'lg:fixed!',
                endSticky && 'lg:top-auto! lg:bottom-0!',
            ]"
        >
            <IconsLine 
                v-for="meteor in meteors"
                class="meteor" 
                :style="{ top: meteor.top, animationDelay: meteor.delay }"
            />
        </div>
        <div class="container relative z-1 text-white lg:px-20">
            <ChipsDivider>ویژگی‌ها</ChipsDivider>
            <h2>
                <template v-if="splitedTitle.length > 1">
                    {{ splitedTitle[0] }}
                    <span class="text-secondary-600">"{{ splitedTitle[1] }}"</span>
                    {{ splitedTitle[2] }}
                </template>
                <template v-else>{{ props.title }}</template>
            </h2>
            <p class="text-body1-semibold text-primary-50 text-center mt-6">{{ props.desc }}</p>
            <div class="relative w-full flex flex-col gap-8 mt-5 lg:mt-12 max-w-[1240px] mx-auto lg:h-400">
                <div 
                    v-for="item, index in props.items"
                    :key="item.title"
                    class="w-full flex justify-center lg:absolute lg:top-0"
                    :class="[
                        (index % 2 === 0) && 'lg:justify-center!',
                        (index === 0 || index === 4) && 'lg:justify-start!',
                        (index === 2) && 'lg:justify-end!',
                    ]"
                    :style="{ marginTop: device.isDesktop.value ? `${ index * 250 }px` : '0' }"
                    data-aos="fade-up" 
                    data-aos-duration="1000"
                >
                    <div class="w-75 lg:w-80 bg-white/10 border border-white rounded-lg px-4 py-6">
                        <NuxtImg 
                            :alt="item.title"
                            :src="item.img"
                            class="w-32 mx-auto"
                        />
                        <span class="block h4 mt-6 mb-3 text-center">{{ item.title }}</span>
                        <p class="text-body2 leading-[1.5rem]! text-primary-50! px-3 lg:px-0">{{ item.desc }}</p>
                    </div>
                </div>
            </div>
        </div>
    </SectionsBase>
</template>

<style scoped>
@reference "~/assets/css/main.css";

.blue-bg-gradient {
    background: linear-gradient(139.35deg, #00204E 8.8%, #003D97 32.61%, #003D97 43.48%, #012C6C 88.8%);
    @apply
    absolute w-full h-full lg:h-[calc(100vh+200px)] overflow-hidden top-0 right-0
}
.blue-bg-gradient::before {
    @apply
    content-[''] absolute w-full h-full top-0 left-0 bg-[url('/img/new/backgrounds/mesh-03.webp')] 
    bg-no-repeat bg-center lg:bg-size-[100%_auto]
}

.meteor {
    animation: meteor infinite linear;
    animation-duration: 8s;
    @apply
    absolute bg-linear-to-r from-white/30 to-transparent -right-25
}
@keyframes meteor {
    to {
        transform: translateX(-110vw);
    }
}
</style>